import _ from 'lodash';
import React from 'react';
import moment from 'moment';
import memoizeOne from 'memoize-one';
import { Grid, Input, Radio } from 'semantic-ui-react';

import styles from './FormPreview.module.css';
import CustomDropdown from '../CustomDropdown';
import FormikEnabledAttachment from './FormikEnabledAttachment';
import FormikEnabledMultiAttachment from './FormikEnabledMultiAttachment';
import FormikEnabledESignatureComponent from './ESignatureComponent/FormikEnabledESignatureComponent';
import FormikEnabledDateTimeComponent from './FormikEnabledDateTimeComponent';
// eslint-disable-next-line import/no-cycle
import ExpandingCombo from './ExpandingCombo';
import {
  findSelectedEntity,
  findRequiredEntities,
  findRequiredOptionForMetatdata,
  findUniqueOptionsIds,
  findValueInAnyParentElement,
  findUniqueOptionsIdsForElementType,
  getUniqueOptionsForDynamicList,
} from '../../../HelperMethods/UtilityFunctions';
import { ATTRIBUTE_TYPE_IN_TASK } from '../../../HelperMethods/enums';
// html element 'input' has these type
export const HTML_INPUT_ELEMENT_TYPE_OPTIONS = {
  TEXT: {
    id: 'text',
    name: 'String',
  },
  NUMBER: {
    id: 'number',
    name: 'Integer',
  },
  DECIMAL: {
    id: 'decimal',
    name: 'Decimal',
  },
};

/* eslint-disable react/jsx-filename-extension */
export const ATTRIBUTE_TYPE = {
  DESCRIPTION: 'description',
  HEADER: 'header',
  SINGLE_CHOICE: 'singlechoice',
  DATE: 'date',
  TIME: 'time',
  INPUT_BOX: 'inputbox',
  SECTION: 'section',
  DROPDOWN: 'dropdown',
  FILE_UPLOAD: 'fileupload',
  SIGNATURE: 'signature',
  DATE_TIME: 'datetime',
  EXPANDING_COMBO: 'expandingcombo',
  METADATA_DYNAMIC_LIST: 'metadatadynamiclist',
  MULTIPLE_FILE_UPLOAD: 'multiplefileupload',
};

export const FORMATTING_CONDITIONS = {
  SHOW_IF: { key: 'showIf', value: 'showIf', text: 'Show If' },
  REQUIRED_IF: { key: 'requiredIf', value: 'requiredIf', text: 'Required If' },
  DEFAULT_IF: { key: 'defaultIf', value: 'defaultIf', text: 'Default If' },
};

const DATE_FORMAT = 'YYYY-MM-DD';
export const IS_REQUIRED_ON_CREATION = 'isRequiredOnCreation';
export const IS_REQUIRED_ON_RESOLUTION = 'isRequired';

export const OPERATOR_OPTIONS_LIST = [
  { key: '=', value: '=', text: '=' },
  { key: '!=', value: '!=', text: '!=' },
  {
    key: '<',
    value: '<',
    text: '<',
    validFor: [HTML_INPUT_ELEMENT_TYPE_OPTIONS.NUMBER.id, HTML_INPUT_ELEMENT_TYPE_OPTIONS.DECIMAL.id],
  },
  {
    key: '>',
    value: '>',
    text: '>',
    validFor: [HTML_INPUT_ELEMENT_TYPE_OPTIONS.NUMBER.id, HTML_INPUT_ELEMENT_TYPE_OPTIONS.DECIMAL.id],
  },
  {
    key: '<=',
    value: '<=',
    text: '<=',
    validFor: [HTML_INPUT_ELEMENT_TYPE_OPTIONS.NUMBER.id, HTML_INPUT_ELEMENT_TYPE_OPTIONS.DECIMAL.id],
  },
  {
    key: '>=',
    value: '>=',
    text: '>=',
    validFor: [HTML_INPUT_ELEMENT_TYPE_OPTIONS.NUMBER.id, HTML_INPUT_ELEMENT_TYPE_OPTIONS.DECIMAL.id],
  },
];

export const METADATA_ASSIGNEE_LIST = { key: 'assigneeList', value: 'assigneeList', text: 'Assignee List' };
export const METADATA_ASSET_LIST = { key: 'assetList', value: 'assetList', text: 'Asset List' };

const filterPicklistOptions = ({
  picklistMappings,
  attributeObj,
  formAttribute = {},
  taskformValues,
  manifestFormValues,
  selectedTasktype,
  allPicklistDataDict,
  data,
  ...props
}) => {
  const { pickListId, mappingId, type: attributeType, metadataInfo, sectionId } = attributeObj;
  const reqPicklist = allPicklistDataDict[pickListId] || {};
  let optionsList = Object.values(reqPicklist.options || {}).map((option) => {
    return { text: option.name, value: option.name, id: option.id, key: option.id, desc: option.description };
  });
  if (attributeType === 'dropdown') {
    const reqMapping = (picklistMappings || {}).find((mapping) => mapping.id === mappingId);
    if (attributeObj.mappingId && reqMapping) {
      for (const parent of attributeObj.parentPickListIds) {
        const { picklistId, type, entity, attributeName, useParentTaskType = false } = parent;
        const selectedEntity = findSelectedEntity(taskformValues, entity, {
          useParentTaskType,
          parentTask: props.parentTask,
          selectedTasktype,
          sectionId,
        });
        if (type === 'metadata' && selectedEntity) {
          // change entity
          const reqEntities = findRequiredEntities({
            ...props,
            selectedTasktype,
            entity,
            selectedEntity,
            useParentTaskType,
            sectionId,
          });
          if (reqEntities) {
            const reqOption = findRequiredOptionForMetatdata({
              picklistDict: allPicklistDataDict,
              picklistId,
              reqEntities,
              attributeName,
            });
            if (reqOption) {
              const optionIds = findUniqueOptionsIds({ reqMapping, pId: pickListId, reqOption });
              optionsList = optionsList.filter((option) => optionIds.includes(option.id));
            }
          }
        } else if (type === 'element') {
          const { val } = findValueInAnyParentElement({
            ...props,
            formValues: manifestFormValues,
            parent,
            attributeType: ATTRIBUTE_TYPE_IN_TASK.FORM_ATTRIBUTE,
            picklistSchema: formAttribute,
            taskformValues,
            formAttribute,
            entType: ATTRIBUTE_TYPE_IN_TASK.FORM_ATTRIBUTE,
            picklistDict: allPicklistDataDict,
            sectionId,
            selectedTasktype,
          });
          if (val) {
            const optionIds = findUniqueOptionsIdsForElementType({ reqMapping, pId: pickListId, val });
            optionsList = optionsList.filter((option) => optionIds.includes(option.id));
          } else {
            optionsList = [];
          }
        }
      }
    }
  } else if (attributeType === 'metadatadynamiclist') {
    const { entity, path, elementId, type, subPath, useParentTaskType = false } = metadataInfo;
    const selectedEntity = findSelectedEntity(taskformValues, entity, {
      useParentTaskType,
      parentTask: props.parentTask,
      selectedTasktype,
      sectionId,
    });

    if (selectedEntity || entity === 'task') {
      const reqEntities = findRequiredEntities({
        ...props,
        path,
        selectedTasktype,
        entity,
        selectedEntity,
        useParentTaskType,
        sectionId,
        attributeObj,
      });
      if (type === 'metadata') {
        optionsList = getUniqueOptionsForDynamicList({ reqEntities, path, subPath, entity });
      } else if (type === 'elementValue') {
        const val = _.get(formAttribute, `${elementId}.value`) || _.get(manifestFormValues, `${elementId}`);
        if (val) {
          const reqPicklistOptions = _.get(formAttribute, `${elementId}.options`) || [];
          const reqOpt = reqPicklistOptions.find((opt) => opt.value === val);
          const reqItem = _.get(reqOpt, `value`) || '';
          if (reqItem) {
            optionsList = getUniqueOptionsForDynamicList({ reqEntities, path, reqItem });
          }
        }
      }
    }
  }
  return optionsList;
};

const getOptionsAndCheckedValueForSingleChoice = (attributeObj, formik, handleChange, print) => {
  const { options = [], disabled } = attributeObj;
  let otherSelected = true;
  const optionList = [];
  let i = 0;
  const optionArrLength = options.length;
  if (print) {
    const obj = (
      <div className="grid-container" style={{ marginLeft: '12px' }}>
        {(options || []).map((option) => {
          const checked = _.get(formik.values, attributeObj.id) === option.name;
          if (checked) {
            otherSelected = false;
          }
          return (
            <Radio
              label={option.name}
              name={attributeObj.name}
              value={option.name}
              style={{ paddingTop: '12px' }}
              disabled={disabled}
              checked={checked} // disableSection
              onChange={(e, data) => handleChange(e, data, ATTRIBUTE_TYPE.SINGLE_CHOICE)}
            />
          );
        })}
      </div>
    );
    optionList.push(obj);
  } else {
    while (i < optionArrLength) {
      const temparray = options.slice(i, i + 6);
      i += 6;
      const obj = (
        <Grid.Column width={_.floor(14 / _.ceil(optionArrLength / 6))} style={{ marginRight: '12px', display: 'grid' }}>
          {/* eslint-disable-next-line no-loop-func */}
          {(temparray || []).map((option) => {
            const checked = _.get(formik.values, attributeObj.id) === option.name;
            if (checked) {
              otherSelected = false;
            }
            return (
              <>
                <Radio
                  label={option.name}
                  name={attributeObj.name}
                  value={option.name}
                  style={{ paddingTop: '12px' }}
                  disabled={disabled}
                  checked={checked} // disableSection
                  onChange={(e, data) => handleChange(e, data, ATTRIBUTE_TYPE.SINGLE_CHOICE)}
                />
              </>
            );
          })}
        </Grid.Column>
      );
      optionList.push(obj);
    }
  }

  return { optionList, otherSelected };
};

const getParsedValue = ({ formValue, condition, formAttributesObj = {} }) => {
  let val = formValue?.[condition?.attribute?.elementId] || formAttributesObj[condition?.attribute?.elementId]?.value;
  if (condition.attribute.inputType === HTML_INPUT_ELEMENT_TYPE_OPTIONS.NUMBER.id) {
    val = parseInt(val);
  } else if (condition.attribute.inputType === HTML_INPUT_ELEMENT_TYPE_OPTIONS.DECIMAL.id) {
    val = parseFloat(val);
  }
  return val;
};

const validateOperators = ({ val, condition }) => {
  switch (condition.operator) {
    case '=': {
      if (condition?.attribute?.inputType === HTML_INPUT_ELEMENT_TYPE_OPTIONS.TEXT.id) {
        if (_.isNil(val) && _.isNil(condition?.value)) {
          return true;
        }
      } else if (val === (condition?.value?.name || condition?.value)) {
        return true;
      }
      return false;
    }
    case '!=': {
      if (condition?.attribute?.inputType === HTML_INPUT_ELEMENT_TYPE_OPTIONS.TEXT.id) {
        if (!_.isNil(val) && _.isNil(condition?.value)) {
          return true;
        }
      } else if (condition?.attribute?.type === ATTRIBUTE_TYPE.DROPDOWN) {
        if (val && !_.isNil(val) && val !== (condition?.value?.name || condition?.value)) {
          return true;
        }
      } else if (val !== (condition?.value?.name || condition?.value)) {
        return true;
      }
      return false;
    }
    case '<': {
      if (!isNaN(val) && val < condition?.value) {
        return true;
      }
      return false;
    }
    case '>': {
      if (!isNaN(val) && val > condition?.value) {
        return true;
      }
      return false;
    }
    case '>=': {
      if (!isNaN(val) && val >= condition?.value) {
        return true;
      }
      return false;
    }

    case '<=': {
      if (!isNaN(val) && val <= condition?.value) {
        return true;
      }
      return false;
    }

    default:
      return false;
  }
};

export const computeIfElementShouldBeShown = (showIfConditions, formValue, formAttributesObj) => {
  if (showIfConditions?.length) {
    for (const condition of showIfConditions) {
      const val = getParsedValue({ formValue, condition, formAttributesObj });
      const shouldShow = validateOperators({ val, condition });
      if (shouldShow) {
        return true;
      }
    }
    return false;
  }
  return true;
};

export function computeDefaultIfValues(defaultIfConditions, formValue, formAttributesObj) {
  if (defaultIfConditions?.length) {
    for (const defaultIfCondition of defaultIfConditions) {
      let doesConditionSatisfy = true;
      const { attributeConditions = [], defaultValue } = defaultIfCondition;
      for (const attributeCondition of attributeConditions) {
        const val = getParsedValue({ formValue, condition: attributeCondition, formAttributesObj });
        const isValid = validateOperators({ val, condition: attributeCondition });
        if (!isValid) {
          doesConditionSatisfy = false;
          break;
        }
      }
      if (doesConditionSatisfy) {
        return defaultValue;
      }
    }
  }
  return null;
}

const inputType = {
  number: 'number',
  text: 'text',
  decimal: 'number',
};

export const ATTRIBUTE_TYPE_INPUT_TYPE_MAPPING = {
  [ATTRIBUTE_TYPE.INPUT_BOX]: ({ attributeObj, formik, handleChange, print, formAttributesObj = {} }) => {
    // eslint-disable-next-line react/prop-types
    const err = _.get(formik, `errors.${attributeObj.id}`);
    const showElement = computeIfElementShouldBeShown(
      attributeObj?.showIfConditions,
      formik?.values,
      formAttributesObj
    );
    if (!showElement) {
      return null;
    }

    return (
      <div className="print-container">
        <label style={{ margin: '12px 24px' }}>
          {attributeObj.label}
          {attributeObj?.taskId && attributeObj?.isRequired && <span className="mandatory-resolve-field">*</span>}
          {!attributeObj?.taskId ? (
            attributeObj.isRequiredOnCreation ? (
              <span className="mandatory-field">*</span>
            ) : attributeObj.isRequired ? (
              <span className="mandatory-resolve-field">*</span>
            ) : (
              ''
            )
          ) : (
            ''
          )}
        </label>
        <div className={`${styles.inputbox} input-box-holder`}>
          {!print && (
            <>
              <Input
                type={inputType[attributeObj.inputType]}
                name={attributeObj.name}
                onBlur={formik.handleBlur}
                onChange={(e, data) => handleChange(e, data, ATTRIBUTE_TYPE.INPUT_BOX)}
                placeholder={attributeObj.label}
                value={_.get(formik.values, attributeObj.id, '')}
                disabled={attributeObj.disabled}
              />
              {err && <div className="errorText">* {err}</div>}
            </>
          )}
          {print && <div className="print-content-text">{_.get(formik.values, attributeObj.id, '')}</div>}
        </div>
      </div>
    );
  },
  [ATTRIBUTE_TYPE.DATE]: ({ attributeObj, formik, handleChange, print, formAttributesObj = {} }) => {
    const err = _.get(formik, `errors.${attributeObj.id}`);
    const minDate = attributeObj.allowPastDate ? '1970-01-01' : moment().format(DATE_FORMAT);
    const showElement = computeIfElementShouldBeShown(
      attributeObj?.showIfConditions,
      formik?.values,
      formAttributesObj
    );
    if (!showElement) {
      return null;
    }
    return (
      <div className="print-container">
        <label style={{ margin: '12px 24px' }}>
          {attributeObj.label}

          {attributeObj?.taskId && attributeObj?.isRequired && <span className="mandatory-resolve-field">*</span>}
          {!attributeObj?.taskId ? (
            attributeObj.isRequiredOnCreation ? (
              <span className="mandatory-field">*</span>
            ) : attributeObj.isRequired ? (
              <span className="mandatory-resolve-field">*</span>
            ) : (
              ''
            )
          ) : (
            ''
          )}
        </label>
        <div className={`${styles.inputbox} input-box-holder`}>
          {!print && (
            <>
              <Input
                type="date"
                name={attributeObj.name}
                onBlur={formik.handleBlur}
                onChange={(e, data) => handleChange(e, data, ATTRIBUTE_TYPE.DATE)}
                value={moment(_.get(formik.values, attributeObj.id, '')).format(DATE_FORMAT)}
                disabled={attributeObj.disabled}
                min={minDate}
              />
              {err && <div className="errorText">* {err}</div>}
            </>
          )}
          {print && <div className="print-content-text">{_.get(formik.values, attributeObj.id, '')}</div>}
        </div>
      </div>
    );
  },
  [ATTRIBUTE_TYPE.TIME]: ({ attributeObj, formik, handleChange, print, formAttributesObj = {} }) => {
    const err = _.get(formik, `errors.${attributeObj.id}`);
    const showElement = computeIfElementShouldBeShown(
      attributeObj?.showIfConditions,
      formik?.values,
      formAttributesObj
    );
    if (!showElement) {
      return null;
    }
    return (
      <div className="print-container">
        <label style={{ margin: '12px 24px' }}>
          {attributeObj.label}
          {attributeObj?.taskId && attributeObj?.isRequired && <span className="mandatory-resolve-field">*</span>}
          {!attributeObj?.taskId ? (
            attributeObj.isRequiredOnCreation ? (
              <span className="mandatory-field">*</span>
            ) : attributeObj.isRequired ? (
              <span className="mandatory-resolve-field">*</span>
            ) : (
              ''
            )
          ) : (
            ''
          )}
        </label>
        <div className={`${styles.inputbox} input-box-holder`}>
          {!print && (
            <>
              <Input
                type="time"
                name={attributeObj.name}
                onBlur={formik.handleBlur}
                onChange={(e, data) => handleChange(e, data, ATTRIBUTE_TYPE.TIME)}
                value={_.get(formik.values, attributeObj.id, '')}
                disabled={attributeObj.disabled}
              />
              {err && <div className="errorText">* {err}</div>}
            </>
          )}
          {print && <div className="print-content-text">{_.get(formik.values, attributeObj.id, '')}</div>}
        </div>
      </div>
    );
  },
  [ATTRIBUTE_TYPE.DESCRIPTION]: ({ attributeObj, formik, formAttributesObj = {} }) => {
    const showElement = computeIfElementShouldBeShown(
      attributeObj?.showIfConditions,
      formik?.values,
      formAttributesObj
    );
    if (!showElement) {
      return null;
    }
    return <p className={`${styles.description} description-holder`}>{attributeObj.label}</p>;
  },
  [ATTRIBUTE_TYPE.HEADER]: ({ attributeObj }) => (
    <h3 className={`${styles.header}  header-holder`}>{attributeObj.label}</h3>
  ),
  [ATTRIBUTE_TYPE.SINGLE_CHOICE]: ({ attributeObj, formik, handleChange, print }) => {
    const { optionList, otherSelected } = getOptionsAndCheckedValueForSingleChoice(
      attributeObj,
      formik,
      handleChange,
      print
    );
    const err = _.get(formik, `errors.${attributeObj.id}`);
    return (
      <div className={`${styles.singleChoiceContainer} single-choice-holder`}>
        <label style={{ fontSize: '12px' }}>
          {attributeObj.label}

          {attributeObj?.taskId && attributeObj?.isRequired && <span className="mandatory-resolve-field">*</span>}
          {!attributeObj?.taskId ? (
            attributeObj.isRequiredOnCreation ? (
              <span className="mandatory-field">*</span>
            ) : attributeObj.isRequired ? (
              <span className="mandatory-resolve-field">*</span>
            ) : (
              ''
            )
          ) : (
            ''
          )}
        </label>
        {!print && (
          <>
            <div className="single-choice-body">
              <div>{optionList}</div>
              {attributeObj.otherAsOption && (
                <div className="other-as-option">
                  <Radio
                    label="Other"
                    name={attributeObj.name}
                    value=""
                    style={{ paddingTop: '12px' }}
                    disabled={attributeObj.disabled}
                    onChange={(e, data) => handleChange(e, data, ATTRIBUTE_TYPE.SINGLE_CHOICE)}
                    checked={otherSelected}
                  />
                  <div className={`${styles.others} single-choice-others-holder`}>
                    <label>If Other selected, specify</label>
                    <Input
                      type="text"
                      name={attributeObj.name}
                      onChange={(e, data) => handleChange(e, data, ATTRIBUTE_TYPE.SINGLE_CHOICE)}
                      placeholder="If Other selected, specify"
                      onBlur={formik.handleBlur}
                      value={otherSelected ? _.get(formik.values, attributeObj.id, '') : ''}
                      disabled={attributeObj.disabled || !otherSelected}
                      style={{ marginTop: '5px' }}
                    />
                  </div>
                </div>
              )}
            </div>
            {err && <div className="errorText">* {err}</div>}
          </>
        )}
        {print && (
          <>
            <div className="single-choice-body">
              {attributeObj.otherAsOption && otherSelected && (
                <>
                  {/* <div className="print-content-dropdown-text">Others</div> */}
                  <div className="print-content-text">{_.get(formik.values, attributeObj.id, '')}</div>
                </>
              )}
              {!otherSelected && <div className="print-content-text">{_.get(formik.values, attributeObj.id, '')}</div>}
            </div>
          </>
        )}
      </div>
    );
  },
  [ATTRIBUTE_TYPE.DROPDOWN]: ({ attributeObj, formik, handleChange, print, formAttributesObj = {} }) => {
    const err = _.get(formik, `errors.${attributeObj.id}`);
    const picklistValue = _.get(formik.values, attributeObj.id, '');
    const selectedOptionDescription =
      _.get(attributeObj, 'showDescription', false) &&
      picklistValue &&
      attributeObj.options.find((op) => op.value === picklistValue)?.desc;
    const showElement = computeIfElementShouldBeShown(
      attributeObj?.showIfConditions,
      formik?.values,
      formAttributesObj
    );
    if (!showElement) {
      return null;
    }
    return (
      <div className="print-container">
        <label style={{ margin: '12px 24px' }}>
          {attributeObj.label}
          {attributeObj?.taskId && attributeObj?.isRequired && <span className="mandatory-resolve-field">*</span>}
          {!attributeObj?.taskId ? (
            attributeObj.isRequiredOnCreation ? (
              <span className="mandatory-field">*</span>
            ) : attributeObj.isRequired ? (
              <span className="mandatory-resolve-field">*</span>
            ) : (
              ''
            )
          ) : (
            ''
          )}
        </label>
        <div className={`${styles.inputbox} dropdown-box-holder`}>
          {!print && attributeObj.sectionDisable && (
            <Input
              type="text"
              name={attributeObj.name}
              onBlur={formik.handleBlur}
              onChange={(e, data) => handleChange(e, data, ATTRIBUTE_TYPE.INPUT_BOX)}
              placeholder={attributeObj.label}
              value={_.get(formik.values, attributeObj.id, '') || attributeObj.value}
              disabled={attributeObj.disabled}
            />
          )}
          {!print && !attributeObj.sectionDisable && (
            <>
              <CustomDropdown
                clearable
                placeholder={attributeObj.label}
                fluid
                search
                selection
                multiple={attributeObj.isMultiple}
                options={attributeObj.options}
                value={_.get(formik.values, attributeObj.id, '') || attributeObj.value}
                onChange={(e, data) => handleChange(e, data, ATTRIBUTE_TYPE.DROPDOWN)}
                disabled={attributeObj.disabled}
                name={attributeObj.name}
              />

              {selectedOptionDescription && (
                <div>
                  <pre style={{ whiteSpace: 'break-spaces' }}>{selectedOptionDescription}</pre>
                </div>
              )}
              {err && <div className="errorText">* {err}</div>}
            </>
          )}
          {print && (
            <>
              <div className="print-content-text">{picklistValue || ''}</div>
            </>
          )}
        </div>
      </div>
    );
  },
  [ATTRIBUTE_TYPE.METADATA_DYNAMIC_LIST]: ({ attributeObj, formik, handleChange, print, onlyForPrint, ...rest }) => {
    const { searchChangeDict = {} } = rest;
    const onSearchChange = searchChangeDict[attributeObj?.metadataInfo?.path];
    const err = _.get(formik, `errors.${attributeObj.id}`);
    let printVal = '';
    if (print) {
      if (attributeObj.isMultiple) {
        printVal = attributeObj.options
          .filter((option) =>
            (_.get(formik.values, attributeObj.id, '') || attributeObj.value || []).includes(option.value)
          )
          .map((val) => val.text)
          .join(', ');
      } else {
        printVal = attributeObj.options
          .filter((option) => (_.get(formik.values, attributeObj.id, '') || attributeObj.value) === option.value)
          .map((val) => val.text)
          .join(', ');
      }
    }
    return (
      <div className="print-container">
        <label style={{ margin: '12px 24px' }}>
          {attributeObj.label}
          {attributeObj?.taskId && attributeObj?.isRequired && <span className="mandatory-resolve-field">*</span>}
          {!attributeObj?.taskId ? (
            attributeObj.isRequiredOnCreation ? (
              <span className="mandatory-field">*</span>
            ) : attributeObj.isRequired ? (
              <span className="mandatory-resolve-field">*</span>
            ) : (
              ''
            )
          ) : (
            ''
          )}
        </label>
        <div className={`${styles.inputbox} dropdown-box-holder`}>
          {!print && attributeObj.sectionDisable && (
            <Input
              type="text"
              name={attributeObj.name}
              onBlur={formik.handleBlur}
              onChange={(e, data) => handleChange(e, data, ATTRIBUTE_TYPE.INPUT_BOX)}
              placeholder={attributeObj.label}
              value={attributeObj.valueName || _.get(formik.values, attributeObj.id, '') || attributeObj.value}
              disabled={attributeObj.disabled}
            />
          )}
          {!print && !attributeObj.sectionDisable && (
            <>
              <CustomDropdown
                clearable
                placeholder={attributeObj.label}
                fluid
                search
                selection
                multiple={attributeObj.isMultiple}
                options={attributeObj.options}
                value={_.get(formik.values, attributeObj.id, '') || attributeObj.value}
                onChange={(e, data) => handleChange(e, data, ATTRIBUTE_TYPE.DROPDOWN)}
                disabled={attributeObj.disabled}
                name={attributeObj.name}
                onSearchChange={onSearchChange}
              />
              {err && <div className="errorText">* {err}</div>}
            </>
          )}

          {print && (
            <>
              <div className="print-content-text">
                {attributeObj.sectionDisable || onlyForPrint ? attributeObj.valueName : printVal}
              </div>
            </>
          )}
        </div>
      </div>
    );
  },
  [ATTRIBUTE_TYPE.FILE_UPLOAD]: (props) => {
    const { attributeObj, formik, formAttributesObj = {} } = props;
    const showElement = computeIfElementShouldBeShown(
      attributeObj?.showIfConditions,
      formik?.values,
      formAttributesObj
    );
    if (!showElement) {
      return null;
    }
    return <FormikEnabledAttachment {...props} />;
  },
  [ATTRIBUTE_TYPE.MULTIPLE_FILE_UPLOAD]: (props) => {
    const { attributeObj, formik, formAttributesObj = {} } = props;
    const showElement = computeIfElementShouldBeShown(
      attributeObj?.showIfConditions,
      formik?.values,
      formAttributesObj
    );
    if (!showElement) {
      return null;
    }
    return <FormikEnabledMultiAttachment {...props} />;
  },
  [ATTRIBUTE_TYPE.SIGNATURE]: FormikEnabledESignatureComponent,
  [ATTRIBUTE_TYPE.DATE_TIME]: (props) => {
    const { attributeObj, formik, formAttributesObj = {} } = props;
    const showElement = computeIfElementShouldBeShown(
      attributeObj?.showIfConditions,
      formik?.values,
      formAttributesObj
    );
    if (!showElement) {
      return null;
    }
    return <FormikEnabledDateTimeComponent {...props} />;
  },
  [ATTRIBUTE_TYPE.EXPANDING_COMBO]: ExpandingCombo,
};

const isSomeTaskValueChanged = (edit, task, taskformValues) => {
  const taskAssigneeIds = task?.assigneeIds?.filter((a) => !a.isDeleted).map((a) => a.assigneeId);
  const taskformAssigneeIds = taskformValues?.s_assigneeid?.filter((a) => a);

  if (
    !edit ||
    task.taskTypeId !== taskformValues.s_tasktype ||
    task.assetId !== (taskformValues.s_asset || taskformValues.s_stop) ||
    !_.isEqual(taskAssigneeIds, taskformAssigneeIds)
  ) {
    return true;
  }
  return false;
};

export const attributeObjForAttributeType = ({
  task,
  item,
  pickListDict,
  readOnly,
  sectionDisable,
  initValue,
  formAttribute,
  sectionSchema,
  picklistMappings,
  taskformValues,
  selectedTasktype,
  allPicklistDataDict,
  edit = false,
  ...props
}) => {
  const attributeObj = {};
  attributeObj.id = item.id;
  attributeObj.name = item.id;
  attributeObj.type = item.type;
  attributeObj.label = item.state.label;
  attributeObj.disabled = readOnly || sectionDisable;
  attributeObj.isRequired = item.state.isRequired;
  attributeObj.isRequiredOnCreation = item.state.isRequiredOnCreation;
  attributeObj.errorMessage = item.state.errorMessage;
  attributeObj.sectionId = sectionSchema?.id;
  attributeObj.sectionDisable = sectionDisable;
  attributeObj.taskId = task?.id;
  if (!attributeObj.isRequired || !attributeObj.isRequiredOnCreation) {
    attributeObj.requiredIfConditions =
      item?.state?.formattingConditions?.[FORMATTING_CONDITIONS.REQUIRED_IF.value] ?? [];
  }
  const showIfConditions = item?.state?.formattingConditions?.[FORMATTING_CONDITIONS.SHOW_IF.value] ?? [];
  if (showIfConditions?.length) {
    attributeObj.showIfConditions = showIfConditions;
  }
  const defaultIfConditions = item?.state?.formattingConditions?.[FORMATTING_CONDITIONS.DEFAULT_IF.value] ?? [];
  if (defaultIfConditions?.length) {
    attributeObj.defaultIfConditions = defaultIfConditions;
  }
  let reqPicklist;
  switch (item.type) {
    case ATTRIBUTE_TYPE.METADATA_DYNAMIC_LIST: {
      attributeObj.isMultiple = item.state.isMultiple;
      attributeObj.metadataInfo = item.state.metadataInfo;
      if ((attributeObj.sectionDisable || props.onlyForPrint) && attributeObj.sectionId) {
        attributeObj.attributeObjformValue = _.get(props, `formData.data.${attributeObj.sectionId}.${attributeObj.id}`);
        if (attributeObj.isMultiple) {
          attributeObj.valueName = _.get(props, `formData.data.${attributeObj.sectionId}.${attributeObj.id}`)
            .map((val) => val.name)
            .join(', ');
        } else {
          attributeObj.valueName =
            _.get(props, `formData.data.${attributeObj.sectionId}.${attributeObj.id}.name`) || '';
        }
      }
      attributeObj.options =
        readOnly || sectionDisable
          ? []
          : filterPicklistOptions({
              ...props,
              picklistMappings,
              attributeObj,
              formAttribute,
              taskformValues,
              manifestFormValues: initValue,
              selectedTasktype,
              allPicklistDataDict,
            });
      if (_.size(attributeObj.options) === 1 && isSomeTaskValueChanged(edit, task, taskformValues)) {
        attributeObj.value = _.get(attributeObj, `options[${0}].value`);
      }
      break;
    }
    case ATTRIBUTE_TYPE.DROPDOWN: {
      attributeObj.otherAsOption = item.state.otherAsOption;
      attributeObj.isMultiple = item.state.isMultiple;
      attributeObj.parentPickListIds = item.state.parentPickListIds;
      attributeObj.pickListId = item.state.pickListId;
      attributeObj.showDescription = item.state.showDescription;
      attributeObj.mappingId = item.state.mappingId;
      attributeObj.defaultValue = item.state?.defaultValue?.name;
      attributeObj.options =
        readOnly || sectionDisable
          ? []
          : filterPicklistOptions({
              ...props,
              picklistMappings,
              attributeObj,
              formAttribute,
              taskformValues,
              manifestFormValues: initValue,
              selectedTasktype,
              allPicklistDataDict,
            });
      if (_.size(attributeObj.options) === 1 && isSomeTaskValueChanged(edit, task, taskformValues)) {
        attributeObj.value = _.get(attributeObj, `options[${0}].value`);
      }

      break;
    }
    case ATTRIBUTE_TYPE.SINGLE_CHOICE:
      reqPicklist = pickListDict[item.state.pickListId];
      attributeObj.options = reqPicklist && Object.values(reqPicklist.options);
      attributeObj.otherAsOption = item.state.otherAsOption;
      break;
    case ATTRIBUTE_TYPE.INPUT_BOX: {
      attributeObj.minVar = item.state.minVar;
      attributeObj.maxVar = item.state.maxVar;
      attributeObj.inputType = item.state.inputType;
      attributeObj.defaultValue = item.state.defaultValue;
      break;
    }
    case ATTRIBUTE_TYPE.DATE:
      attributeObj.allowPastDate = item.state.allowPastDate;
      attributeObj.currentDateAsDefaultValue = item.state.currentDateAsDefaultValue;
      break;
    case ATTRIBUTE_TYPE.SECTION:
      attributeObj.sequence = item.state.sequence;
      break;
    case ATTRIBUTE_TYPE.FILE_UPLOAD:
      attributeObj.allowedFormat = item.state.allowedFormat;
      attributeObj.sizeLimit = item.state.sizeLimit;
      break;
    case ATTRIBUTE_TYPE.MULTIPLE_FILE_UPLOAD:
      attributeObj.allowedFormat = item.state.allowedFormat;
      attributeObj.sizeLimit = item.state.sizeLimit;
      break;
    case ATTRIBUTE_TYPE.SIGNATURE:
      attributeObj.clause = item.state.clause;
      attributeObj.optout = item.state.optout;
      attributeObj.showAccept = item.state.showAccept;
      break;
    default:
  }
  return attributeObj;
};

export function allFormElementForEachFunc(elements, func) {
  const allElementsArray = [elements];
  const parents = [null];
  for (let i = 0; i < allElementsArray.length; i += 1) {
    const elementsArray = allElementsArray[i];
    const parent = parents[i];
    for (const element of elementsArray) {
      if (element.elements) {
        allElementsArray.push(element.elements);
        parents.push({ ...element, parent });
      }
      func(element, parent);
    }
  }
}

function findSectionInAncestors(ancestor) {
  if (!ancestor) return null;
  if (ancestor.type === ATTRIBUTE_TYPE.SECTION) {
    return ancestor;
  }
  return findSectionInAncestors(ancestor.parent);
}

export function getCompatibleFormValue({ item, dataValue }) {
  if (item.type === ATTRIBUTE_TYPE.INPUT_BOX) {
    let value = dataValue;
    if (dataValue && typeof dataValue === 'object') {
      value = dataValue?.name ?? '';
    }
    return value ?? '';
  }
  if (
    item.type === ATTRIBUTE_TYPE.DATE ||
    item.type === ATTRIBUTE_TYPE.TIME ||
    item.type === ATTRIBUTE_TYPE.FILE_UPLOAD ||
    item.type === ATTRIBUTE_TYPE.SIGNATURE
  ) {
    return dataValue ?? '';
  }
  if (item.type === ATTRIBUTE_TYPE.DROPDOWN || item.type === ATTRIBUTE_TYPE.SINGLE_CHOICE) {
    let picklistValue = dataValue ?? (item.state.isMultiple ? [] : '');
    if (item.state.isMultiple) {
      if (Array.isArray(picklistValue) && picklistValue.length > 0) {
        picklistValue = picklistValue.map((singleValue) => singleValue?.name || singleValue);
      } else {
        picklistValue = [];
      }
    } else if (picklistValue && typeof picklistValue === 'object') {
      picklistValue = picklistValue.name;
    }
    return picklistValue;
  }
  if (item.type === ATTRIBUTE_TYPE.METADATA_DYNAMIC_LIST) {
    let picklistValue = dataValue ?? (item.state.isMultiple ? [] : '');
    if (item.state.isMultiple) {
      if (Array.isArray(picklistValue) && picklistValue.length > 0) {
        picklistValue = picklistValue.map((singleValue) => singleValue?.id || singleValue);
      } else {
        picklistValue = [];
      }
    } else if (picklistValue && typeof picklistValue === 'object') {
      picklistValue = picklistValue.id;
    }
    return picklistValue;
  }
  if (item.type === ATTRIBUTE_TYPE.DATE_TIME) {
    return dataValue ?? { date: '', time: '' };
  }
  if (item.type === ATTRIBUTE_TYPE.MULTIPLE_FILE_UPLOAD) {
    if (dataValue !== null && typeof dataValue === 'object' && !Array.isArray(dataValue)) {
      return [dataValue];
    }
    return dataValue ?? [];
  }
  return '';
}

function getParentValuePath(parent) {
  if (!parent) return '';
  const parentValuePath = getParentValuePath(parent.parent);
  return `${parentValuePath ? `${parentValuePath}.` : ''}${parent.id}`;
}

const mapValues = ({
  task,
  schema,
  data = {},
  pickListDict,
  initValue,
  formAttribute,
  readOnly,
  selectedTasktype,
  picklistMappings,
  taskformValues,
  allPicklistDataDict,
  isFormikValue = false,
  ...props
}) => {
  allFormElementForEachFunc(schema.elements, (item, parent) => {
    const sectionSchemaOfFormElement = findSectionInAncestors(parent);
    let disableSectionElements = false;
    if (sectionSchemaOfFormElement && task.id) {
      disableSectionElements =
        task && task.parentTaskId && task.formSectionId
          ? sectionSchemaOfFormElement?.id !== task.formSectionId
          : !!_.get(selectedTasktype, 'originalobject.isParent', null);
    }

    const attributeObj = attributeObjForAttributeType({
      ...props,
      task,
      item,
      pickListDict,
      readOnly,
      sectionDisable: disableSectionElements,
      initValue,
      formAttribute,
      sectionSchema: sectionSchemaOfFormElement,
      picklistMappings,
      taskformValues,
      selectedTasktype,
      allPicklistDataDict,
    });
    item.attributeObj = attributeObj;
    formAttribute[item.id] = attributeObj;
    if (parent?.type !== ATTRIBUTE_TYPE.EXPANDING_COMBO) {
      const parentValuePath = getParentValuePath(parent);
      let valueGetPath = `${parentValuePath ? `${parentValuePath}.` : ''}${item.id}`;
      if (isFormikValue) {
        valueGetPath = item.id;
      }
      let dataValue = attributeObj.value || _.get(data, valueGetPath);
      if ((_.isNil(dataValue) || dataValue === '') && attributeObj.type === ATTRIBUTE_TYPE.INPUT_BOX) {
        dataValue = attributeObj.defaultValue;
      }
      if (!dataValue && attributeObj.type === ATTRIBUTE_TYPE.DATE && attributeObj.currentDateAsDefaultValue) {
        dataValue = moment().format(DATE_FORMAT);
      }
      if (!dataValue && attributeObj.type === ATTRIBUTE_TYPE.DROPDOWN && attributeObj.defaultValue) {
        dataValue = attributeObj.defaultValue;
      }
      _.set(initValue, item.id, getCompatibleFormValue({ item, dataValue }));
    } else {
      const parentValuePath = getParentValuePath(parent);
      const expandingComboValue = _.get(data, parentValuePath);
      if (!expandingComboValue?.length) {
        _.set(initValue, `${parent.id}[0].${item.id}`, getCompatibleFormValue({ item }));
      } else {
        for (let i = 0; i < expandingComboValue.length; i += 1) {
          const valueGetPath = `${parentValuePath}[${i}].${item.id}`;
          const dataValue = _.get(data, valueGetPath);
          _.set(initValue, `${parent.id}[${i}].${item.id}`, getCompatibleFormValue({ item, dataValue }));
        }
      }
    }
  });
  return { initValue, formAttribute };
};

export const mapValuesToSchema = memoizeOne(
  ({
    task,
    schema,
    values = {},
    picklist = [],
    readOnly,
    selectedTasktype,
    picklistMappings,
    taskformValues,
    allPicklistData,
    ...props
  }) => {
    const cloneSchema = _.cloneDeep(schema);
    const cloneValues = _.cloneDeep(values);
    const pickListDict = _.keyBy(picklist, 'id', {});
    const allPicklistDataDict = _.keyBy(allPicklistData, 'id', {});
    const initValue = {};
    const formAttribute = {};
    const { initValue: formikInitValue, formAttribute: formAttributeValue } = mapValues({
      ...props,
      task,
      schema: cloneSchema,
      data: cloneValues.data,
      isFormikValue: cloneValues.isFormikValue,
      pickListDict,
      initValue,
      formAttribute,
      readOnly,
      selectedTasktype,
      picklistMappings,
      taskformValues,
      allPicklistDataDict,
    });
    return { schema: cloneSchema.elements, formikInitValue, formAttributeValue };
  }
);

export const getPicklistIds = (schema, picklistIds) => {
  schema.elements.forEach((item) => {
    if (item.elements) {
      getPicklistIds(item, picklistIds);
    } else if (item.type === ATTRIBUTE_TYPE.SINGLE_CHOICE || item.type === ATTRIBUTE_TYPE.DROPDOWN) {
      const id = _.get(item, `state.pickListId`, '');
      picklistIds.push(id);
    }
  });
  return picklistIds;
};

export const getFormElementsForAssetList = (schema, elements, sectionId = '') => {
  schema.elements.forEach((item) => {
    if (item.elements) {
      getFormElementsForAssetList(item, elements, item.id);
    } else if (
      item.type === ATTRIBUTE_TYPE.METADATA_DYNAMIC_LIST &&
      item?.state?.metadataInfo?.entity === 'task' &&
      item?.state?.metadataInfo?.path === 'assetList'
    ) {
      elements.push({ elemId: item.id, sectionId });
    }
  });
  return elements;
};

const buildFormData = (schema, values, data, picklistDict, formAttribute) => {
  schema.forEach((item) => {
    const isElementShown = computeIfElementShouldBeShown(
      formAttribute?.[item.id]?.showIfConditions ?? [],
      values,
      formAttribute
    );
    if (item.elements && item.type === ATTRIBUTE_TYPE.SECTION) {
      const sectionId = item.id;
      data[sectionId] = {};
      buildFormData(item.elements, values, data[sectionId], picklistDict, formAttribute);
    } else if (item.elements && item.type === ATTRIBUTE_TYPE.EXPANDING_COMBO) {
      const expandingComboId = item.id;
      data[expandingComboId] = [];
      const expandingComboValues = _.get(values, expandingComboId);
      for (let i = 0; i < expandingComboValues.length; i += 1) {
        data[expandingComboId][i] = {};
        buildFormData(item.elements, expandingComboValues[i], data[expandingComboId][i], picklistDict, formAttribute);
      }
    } else if (item.type === ATTRIBUTE_TYPE.SIGNATURE) {
      const value = values[item.id];
      data[item.id] = value || null;
    } else if (item.type === ATTRIBUTE_TYPE.INPUT_BOX) {
      if (!isElementShown) {
        data[item.id] = null;
      } else if (item.state.inputType === HTML_INPUT_ELEMENT_TYPE_OPTIONS.NUMBER.id) {
        data[item.id] = isNaN(parseInt(values[item.id])) ? null : parseInt(values[item.id]);
      } else if (item.state.inputType === HTML_INPUT_ELEMENT_TYPE_OPTIONS.DECIMAL.id) {
        data[item.id] = isNaN(parseFloat(values[item.id])) ? null : parseFloat(values[item.id]);
      } else {
        data[item.id] = values[item.id] || null;
      }
    } else if (
      item.type === ATTRIBUTE_TYPE.FILE_UPLOAD ||
      item.type === ATTRIBUTE_TYPE.DATE ||
      item.type === ATTRIBUTE_TYPE.TIME
    ) {
      let value = null;
      if (isElementShown) {
        value = values[item.id];
      }
      data[item.id] = value || null;
    } else if (item.type === ATTRIBUTE_TYPE.SINGLE_CHOICE || item.type === ATTRIBUTE_TYPE.DROPDOWN) {
      let picklistValue;
      if (isElementShown) {
        const picklistOptionName = values[item.id] || _.get(formAttribute, `${item.id}.value`, '');
        const picklistId = item.state.pickListId;
        const picklistOptionsKeyedByName = _.keyBy(
          Object.values(_.get(picklistDict, `${picklistId}.options`, {})),
          'name'
        );
        if (item.state.isMultiple) {
          const picklistOptionNames =
            !picklistOptionName || !Array.isArray(picklistOptionName) ? [] : picklistOptionName;
          picklistValue = picklistOptionNames.map((optionName) => {
            const picklistOptionId = picklistOptionsKeyedByName[optionName]?.id ?? null;
            return {
              id: picklistOptionId,
              name: optionName || null,
            };
          });
        } else {
          const picklistOptionId = picklistOptionsKeyedByName[picklistOptionName]?.id ?? null;
          picklistValue = {
            id: picklistOptionId,
            name: picklistOptionName || null,
          };
        }
      }
      data[item.id] = picklistValue;
    } else if (item.type === ATTRIBUTE_TYPE.METADATA_DYNAMIC_LIST) {
      let picklistValue;
      if (item?.attributeObj?.sectionDisable) {
        picklistValue = item?.attributeObj?.attributeObjformValue ?? {};
      } else {
        const picklistOptionId = values[item.id] || _.get(formAttribute, `${item.id}.value`, '');
        const picklistOptionsKeyedById = _.keyBy(_.get(formAttribute, `${item.id}.options`, {}), 'value');
        if (item.state.isMultiple) {
          const picklistOptionIds = !picklistOptionId || !Array.isArray(picklistOptionId) ? [] : picklistOptionId;
          picklistValue = picklistOptionIds.map((optionId) => {
            const picklistOptionText = picklistOptionsKeyedById[optionId]?.text ?? null;
            return {
              id: optionId,
              name: picklistOptionText || null,
            };
          });
        } else {
          const picklistOptionText = picklistOptionsKeyedById[picklistOptionId]?.text ?? null;
          picklistValue = {
            id: picklistOptionId,
            name: picklistOptionText || null,
          };
        }
      }

      data[item.id] = picklistValue;
    } else if (item.type === ATTRIBUTE_TYPE.DATE_TIME) {
      let value = { date: null, time: null };
      if (isElementShown) {
        const { date, time } = values[item.id];
        value = { date: date || null, time: time || null };
      }
      data[item.id] = value;
    } else if (item.type === ATTRIBUTE_TYPE.MULTIPLE_FILE_UPLOAD) {
      let value = [];
      if (isElementShown) {
        value = values[item.id];
      }
      data[item.id] = value || [];
    }
  });
  return data;
};

const getUpdatedSignatures = (schema, oldData = {}, newData = {}, path = '') => {
  const updatedSignatures = [];
  schema.forEach((item) => {
    if (item.elements && item.type === ATTRIBUTE_TYPE.SECTION) {
      updatedSignatures.push(
        ...getUpdatedSignatures(item.elements, oldData[item.id], newData[item.id], `${path}${item.id}.`)
      );
    } else if (item.elements && item.type === ATTRIBUTE_TYPE.EXPANDING_COMBO) {
      const oldExpandingComboValues = oldData[item.id] ?? [];
      const newExpandingComboValues = newData[item.id] ?? [];
      const maxLoopCounter = Math.max(oldExpandingComboValues.length, newExpandingComboValues.length);
      for (let i = 0; i < maxLoopCounter; i += 1) {
        updatedSignatures.push(
          ...getUpdatedSignatures(
            item.elements,
            oldExpandingComboValues[i],
            newExpandingComboValues[i],
            `${path}${item.id}[${i}].`
          )
        );
      }
    } else if (item.type === ATTRIBUTE_TYPE.SIGNATURE) {
      const newSignatureObj = newData[item.id] ?? null;
      const oldSignatureObj = oldData[item.id] ?? null;
      if (!_.isEqual(newSignatureObj, oldSignatureObj)) {
        updatedSignatures.push({ ...(newData[item.id] ?? {}), path: `${path}${item.id}` });
      }
    }
  });
  return updatedSignatures;
};

export const mapValuesToFormDataAndUdpatedSignatures = ({
  schema,
  formValues,
  formData,
  originalSchema,
  picklistDict,
  formAttribute,
}) => {
  let cloneFormData = _.cloneDeep(formData);
  const updatedData = buildFormData(schema, formValues, {}, picklistDict, formAttribute);
  if (!cloneFormData || !Object.keys(cloneFormData).length) {
    cloneFormData = { type: 'formdata', schemaId: originalSchema.id, fieldId: originalSchema.fieldId };
  }
  const updatedSignatures = getUpdatedSignatures(schema, formData?.data, updatedData);
  cloneFormData.data = updatedData;
  return { data: cloneFormData, updatedSignatures };
};

export const getSectionIds = (form, sectionIds) => {
  form.schema.forEach((item) => {
    if (item.type === ATTRIBUTE_TYPE.SECTION) {
      sectionIds.push(item.id);
    }
  });
  return sectionIds;
};

export function updateComputedDefaultIfValue({
  formSchemaState,
  formVal,
  formik,
  handleFormSchema,
  data,
  updateState = true,
}) {
  const clonedFormSchemaState = _.cloneDeep(formSchemaState);
  const formSchemaElements = clonedFormSchemaState.schema;
  let formValClone = _.cloneDeep(formVal);
  allFormElementForEachFunc(formSchemaElements, (formSchemaElement) => {
    const reqAttribute = formSchemaElement.attributeObj;
    const { name } = data;
    if (name !== reqAttribute.name && reqAttribute?.defaultIfConditions?.length) {
      // calculate the default if condition
      const dataValue = computeDefaultIfValues(reqAttribute.defaultIfConditions, formVal, reqAttribute);
      if (dataValue) {
        let updateValue = dataValue;
        if (reqAttribute.type === ATTRIBUTE_TYPE.DROPDOWN) {
          updateValue = dataValue.name;
        }
        formik.setFieldValue(reqAttribute.name, updateValue, false);
        formik.setFieldTouched(reqAttribute.name, true, false);
        formValClone = { ...formValClone, [reqAttribute.name]: updateValue };
      } else {
        const value = formValClone[reqAttribute.name] || '';
        if (reqAttribute.type === ATTRIBUTE_TYPE.DROPDOWN) {
          reqAttribute.value = value;
        }
        formik.setFieldValue(reqAttribute.name, value, false);
        formik.setFieldTouched(reqAttribute.name, true, false);
        formValClone = { ...formValClone, [reqAttribute.name]: value };
      }
    }
  });
  const updatedInfo = { ...clonedFormSchemaState, formikInitValue: formValClone };
  if (updateState) {
    handleFormSchema(updatedInfo);
  }
  return updatedInfo;
}

export const filterChildPicklist = ({
  formVal,
  formSchemaState,
  allPicklistData,
  taskformValues,
  picklistMappings,
  selectedTasktype,
  data,
  handleFormSchema,
  formik,
  ...props
}) => {
  const clonedFormSchemaState = _.cloneDeep(formSchemaState);
  const formSchemaElements = clonedFormSchemaState.schema;
  let cloneFormVal = _.cloneDeep(formVal);
  allFormElementForEachFunc(formSchemaElements, (formSchemaElement) => {
    const reqAttribute = formSchemaElement.attributeObj;
    const allPicklistDataDict = _.keyBy(allPicklistData, 'id');
    if (reqAttribute && !reqAttribute.sectionDisable) {
      const optionsList = filterPicklistOptions({
        ...props,
        picklistMappings,
        attributeObj: reqAttribute,
        formAttribute: clonedFormSchemaState.formAttributeValue,
        taskformValues,
        manifestFormValues: cloneFormVal,
        selectedTasktype,
        allPicklistDataDict,
      });
      if (reqAttribute.type === 'dropdown' || reqAttribute.type === 'metadatadynamiclist') {
        reqAttribute.options = optionsList;

        if (optionsList && optionsList.length === 1 && data && data.name !== reqAttribute.name) {
          const [option] = optionsList;
          const reqVal = option ? option.value : '';
          if (reqVal) {
            reqAttribute.value = reqVal;
            cloneFormVal = { ...cloneFormVal, [reqAttribute.name]: reqVal };
            formik.setFieldValue(reqAttribute.name, reqVal, false);
          }
        } else if (optionsList && optionsList.length === 0) {
          reqAttribute.value = '';
          formik.setFieldValue(reqAttribute.name, '', false);
          cloneFormVal = { ...cloneFormVal, [reqAttribute.name]: '' };
        } else if (data.name !== reqAttribute.name && optionsList?.length > 1) {
          let reqOption = '';
          if (reqAttribute.isMultiple) {
            reqOption = optionsList.find((option) =>
              (reqAttribute.value || formVal[reqAttribute.name] || []).includes(option.value)
            );
          } else {
            reqOption = optionsList.find(
              (option) => option.value === (reqAttribute.value || formVal[reqAttribute.name])
            );
          }
          if (!reqOption) {
            reqAttribute.value = '';
            formik.setFieldValue(reqAttribute.name, '', false);
            cloneFormVal = { ...cloneFormVal, [reqAttribute.name]: '' };
          }
        } else if (data && data.name === reqAttribute.name) {
          reqAttribute.value = formVal[reqAttribute.name];
        }
      }
    }
  });
  const updatedState = updateComputedDefaultIfValue({
    formSchemaState: clonedFormSchemaState,
    formVal: cloneFormVal,
    formik,
    handleFormSchema,
    data,
    updateState: false,
  });
  handleFormSchema({ ...updatedState });
};

export const computeIfElementIsRequired = (formAttributesObj = {}, attributeObj = {}, formValue = {}) => {
  const { requiredIfConditions = [] } = attributeObj;
  const isElementShown = computeIfElementShouldBeShown(
    formAttributesObj?.[attributeObj.id]?.showIfConditions ?? [],
    formValue,
    formAttributesObj
  );
  if (isElementShown && requiredIfConditions?.length) {
    for (const condition of requiredIfConditions) {
      const val = getParsedValue({ formValue, condition, formAttributesObj });
      const isRequired = validateOperators({ val, condition });
      if (isRequired) {
        return { conditionRequired: true, isElementShown };
      }
    }
  }
  return { conditionRequired: false, isElementShown };
};
